import React from 'react';
import { BookOpen, Cpu, Image as ImageIcon, MessageSquare, StickyNote } from 'lucide-react';

export type Stage1Tab = 'summary' | 'tech-basics' | 'figures' | 'qa' | 'notes';

interface TabBarProps {
  activeTab: Stage1Tab;
  onTabChange: (tab: Stage1Tab) => void;
  /** Number of Summary fields the drafter has edited away from the AI
   *  original. Rendered as a small badge next to the tab label; hidden
   *  when zero so new matters don't carry an irrelevant "0". */
  summaryEditedCount: number;
  /** Estimated read time for the Technology Basics content, e.g.
   *  "3 min". Shown in muted text next to the label; hidden when empty. */
  techBasicsReadTime: string;
  /** Count of persisted figures (matter_figures rows with an image on
   *  disk). Badge hidden when zero so a fresh matter doesn't carry an
   *  empty "0" before any IDF has been uploaded. */
  figuresCount: number;
  /** Count of Q&A questions still awaiting an answer. Badge hidden when
   *  zero. */
  qaUnansweredCount: number;
  /** Character count of the Drafter Notes textarea. Muted text next to
   *  the label (not a badge per spec); hidden when zero. */
  notesCharCount: number;
  /** Last auto-save timestamp. Rendered far-right as
   *  "auto-saved {HH:MM}". Null → the slot is blank so the tab row
   *  doesn't jump as the value appears. */
  autoSavedAt: Date | null;
}

interface TabDef {
  id: Stage1Tab;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  /** Badge content (rounded pill). Null = no badge. */
  badge: React.ReactNode | null;
  /** Tooltip on the badge, saying what the number counts. */
  badgeTitle?: string;
  /** Muted-text content rendered after the label — for "3 min" and the
   *  character count. Null = nothing rendered. */
  muted: React.ReactNode | null;
}

function formatAutoSaved(ts: Date | null): string {
  if (!ts) return '';
  const hh = ts.getHours().toString().padStart(2, '0');
  const mm = ts.getMinutes().toString().padStart(2, '0');
  return `auto-saved ${hh}:${mm}`;
}

export default function TabBar({
  activeTab,
  onTabChange,
  summaryEditedCount,
  techBasicsReadTime,
  figuresCount,
  qaUnansweredCount,
  notesCharCount,
  autoSavedAt,
}: TabBarProps) {
  const tabs: TabDef[] = [
    {
      id: 'summary',
      label: 'Summary',
      icon: BookOpen,
      badge: summaryEditedCount > 0 ? summaryEditedCount : null,
      muted: null,
    },
    {
      id: 'tech-basics',
      label: 'Technology Basics',
      icon: Cpu,
      badge: null,
      muted: techBasicsReadTime || null,
    },
    // Figures tab sits between Technology Basics and Smart Q&A so
    // the visual flow mirrors how a drafter consumes the IDF: read
    // the tech, then check what was extracted (figures), then answer
    // the model's questions about it.
    {
      id: 'figures',
      label: 'Figures',
      icon: ImageIcon,
      badge: figuresCount > 0 ? figuresCount : null,
      muted: null,
    },
    {
      id: 'qa',
      label: 'Smart Q\u0026A',
      icon: MessageSquare,
      badge: qaUnansweredCount > 0 ? qaUnansweredCount : null,
      badgeTitle: `${qaUnansweredCount} question${qaUnansweredCount === 1 ? '' : 's'} not answered yet`,
      muted: null,
    },
    {
      id: 'notes',
      label: 'Drafter Notes',
      icon: StickyNote,
      badge: null,
      muted: notesCharCount > 0 ? notesCharCount.toLocaleString() : null,
    },
  ];

  return (
    <div data-testid="stage-1-tab-bar" className="stage1-tab-bar">
      {tabs.map((t) => {
        const active = t.id === activeTab;
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            type="button"
            data-testid={`stage-1-tab-${t.id}`}
            aria-selected={active}
            onClick={() => onTabChange(t.id)}
            className={
              'relative px-3 py-2 -mb-px text-xs font-medium border-b-2 transition-colors ' +
              'flex items-center gap-1.5 ' +
              (active
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-zinc-600 hover:text-zinc-900')
            }
          >
            <Icon size={13} className={active ? 'text-[#002FA7]' : 'text-zinc-400'} />
            <span>{t.label}</span>
            {t.badge !== null && (
              <span
                data-testid={`stage-1-tab-${t.id}-badge`}
                title={t.badgeTitle}
                className={
                  'inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full text-[10px] font-medium ' +
                  (active
                    ? 'bg-[#002FA7] text-white'
                    : 'bg-zinc-200 text-zinc-700')
                }
              >
                {t.badge}
              </span>
            )}
            {t.muted && (
              <span
                data-testid={`stage-1-tab-${t.id}-muted`}
                className="text-[10px] text-zinc-400 font-normal"
              >
                {t.muted}
              </span>
            )}
          </button>
        );
      })}

      <div
        data-testid="stage-1-tab-autosaved"
        className="ml-auto text-[10px] font-mono text-zinc-400 uppercase tracking-wider"
      >
        {formatAutoSaved(autoSavedAt)}
      </div>
    </div>
  );
}
